<template>
  <div class="form-field">
    <div class="relative">
      <div v-if="prefixIcon" class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none z-10">
        <i :class="[prefixIcon, 'text-gray-400']"></i>
      </div>
      <Datepicker
        v-model="dateRange"
        :placeholder="placeholder"
        :teleport="true"
        range
        autoApply
        :enable-time-picker="enableTimePicker"
        :format="format"
        :text-input="true"
        :input-class-name="inputClass"
        :locale="locale"
        :preview-format="format"
        :time-picker-inline="true"
        
        @update:model-value="handleDateChange"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

const props = defineProps({
  id: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: '选择日期范围'
  },
  prefixIcon: {
    type: String,
    default: ''
  },
  startField: {
    type: String,
    default: 'start_time'
  },
  endField: {
    type: String,
    default: 'end_time'
  },
  modelValue: {
    type: Object,
    default: () => ({})
  },
  format: {
    type: String,
    default: 'yyyy-MM-dd HH:mm:ss'
  },
  enableTimePicker: {
    type: Boolean,
    default: true
  },
  locale: {
    type: String,
    default: 'zh'
  },
  options: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['change', 'update:modelValue']);

const dateRange = ref(null);

// Input class based on whether prefix icon exists
const inputClass = computed(() => {
  return props.prefixIcon 
    ? 'form-input pl-10'
    : 'form-input';
});

// Initialize date range from model value if available
watch(() => props.modelValue, (newValue) => {
  if (newValue && newValue[props.startField] && newValue[props.endField]) {
    try {
      const startDate = new Date(newValue[props.startField]);
      const endDate = new Date(newValue[props.endField]);
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        dateRange.value = [startDate, endDate];
        console.log('日期选择器初始化:', [startDate, endDate]);
      }
    } catch (err) {
      console.error('日期格式化错误:', err);
    }
  } else {
    dateRange.value = null;
  }
}, { immediate: true });

// Watch for changes in startField and endField to reset the picker when they're cleared
watch(() => [props.modelValue[props.startField], props.modelValue[props.endField]], ([startVal, endVal]) => {
  if ((!startVal || startVal === '') && (!endVal || endVal === '')) {
    dateRange.value = null;
  }
}, { immediate: true });

function handleDateChange(value) {
  if (!value) {
    const update = {
      [props.startField]: '',
      [props.endField]: ''
    };
    emit('change', update);
    emit('update:modelValue', update);
    return;
  }

  const [startDate, endDate] = value;
  
  // Format dates as strings with time
  const formatDateWithTime = (date) => {
    if (!date) return '';
    
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };
  
  const formattedStartDate = formatDateWithTime(startDate);
  const formattedEndDate = formatDateWithTime(endDate);
  
  // Create update object
  const update = {
    [props.startField]: formattedStartDate,
    [props.endField]: formattedEndDate
  };
  
  // Emit both events for compatibility
  emit('change', update);
  emit('update:modelValue', update);
  
  console.log('日期范围已更改:', update);
}
</script>

<style scoped>
::v-deep(.dp__input) {
  @apply block w-full rounded-md border-gray-300 shadow-sm 
  text-gray-700 placeholder-gray-400 sm:text-sm py-2.5
  transition-all duration-200 ease-in-out
  focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500;
  border: none !important;
  padding-left: 40px !important;
}

::v-deep(.dp__input::placeholder) {
  @apply text-gray-600;
}
.form-field {
  @apply w-full;
}

/* 自定义vue-datepicker样式以匹配其他表单组件 */
:deep(.dp__main) {
  font-family: inherit;
}

:deep(.dp__input:hover) {
  border-color: #9ca3af;
}

:deep(.dp__input:focus) {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
}

:deep(.dp__input:disabled) {
  background-color: #f3f4f6;
  color: #6b7280;
  cursor: not-allowed;
}

:deep(.dp__outer_menu) {
  z-index: 50;
}

:deep(.dp__theme_light) {
  --dp-background-color: #fff;
  --dp-text-color: #374151;
  --dp-hover-color: #f3f4f6;
  --dp-hover-text-color: #111827;
  --dp-hover-icon-color: #6b7280;
  --dp-primary-color: #1aaa55; /* 主题色，使用与其他组件一致的颜色 */
  --dp-primary-text-color: #ffffff;
  --dp-secondary-color: #e5e7eb;
  --dp-border-color: #d1d5db;
  --dp-menu-border-color: #d1d5db;
  --dp-border-color-hover: #9ca3af;
  --dp-disabled-color: #f3f4f6;
  --dp-scroll-bar-background: #f3f4f6;
  --dp-scroll-bar-color: #9ca3af;
  --dp-success-color: #10b981;
  --dp-success-color-disabled: #d1fae5;
  --dp-icon-color: #6b7280;
  --dp-danger-color: #ef4444;
}

:deep(.pl-10 .dp__input) {
  padding-left: 2.5rem;
}

/* 调整日期选择器下拉面板样式 */
:deep(.dp__menu) {
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  padding: 0.5rem;
}

/* 调整范围内的日期单元格样式 */
:deep(.dp__range_start), :deep(.dp__range_end) {
  background-color: var(--dp-primary-color);
}

:deep(.dp__range_between) {
  background-color: rgba(26, 170, 85, 0.1); /* 使用主题色的淡色作为范围背景 */
  color: #333;
}

:deep(.dp__active_date) {
  background-color: var(--dp-primary-color);
  color: white;
}

/* 时间选择器样式 */
:deep(.dp__time_picker) {
  border-top: 1px solid var(--dp-border-color);
  padding-top: 0.5rem;
  margin-top: 0.5rem;
}

:deep(.dp__time_input) {
  @apply border-gray-300 rounded-md shadow-sm text-sm;
}

:deep(.dp__time_col_reg) {
  @apply text-center;
}
</style> 